/**
 * Category B Cheap-Win ActionDefs
 *
 * From `gibbs-bmaps-coverage-category-b` (scaffolded 2026-05-03).
 * Five user-facing UserActions that fall out of the parent
 * capability-coverage study's cluster scope only because they don't
 * fit cleanly into one cluster's theme.
 *
 * **Path B chosen for `CallProteinFolding`.** Inspection of
 * `proto/src/cmds/ConnectedDataCmds.js` confirms `FoldAndLoad` wraps
 * `CallProteinFolding`: foldAndLoad calls `prepareFoldingArgs` →
 * `callProteinFolding` → loads the result into the workspace.
 * `CallProteinFolding` returns raw fold results without loading; for
 * Gibbs's purpose (get a folded protein into the workspace) the
 * existing `fold-protein` → `FoldAndLoad` binding is correct.
 * `CallProteinFolding` stays unbound by design and is documented in
 * the parent's followups.md as "reached transitively".
 *
 * @module gibbs/registry/actions/category-b-extras
 */

import { param, pre } from '../schema';

// ─── Display extras ────────────────────────────────────────────────────────

export const setDipoles = {
    id: 'set-dipoles',
    name: 'Set Ligand Dipoles',
    category: 'Display',
    description: 'Toggle ligand dipole vector visualization in the 3D viewer. Use when the user wants to see the electrostatic dipoles overlaid on the active compound.',
    inputs: [],
    outputs: [],
    parameters: [
        param('dipoleMode', 'string', 'Dipole rendering mode (off, vectors, charges)', true, { defaultValue: 'vectors' }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded for dipoles to apply'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetDipoles',
    serverCommand: null,
    jobType: null,
};

export const setFragmentEnergyFilter = {
    id: 'set-fragment-energy-filter',
    name: 'Set Fragment Energy Filter',
    category: 'Fragment Operations',
    description: 'Set the energy threshold for filtering which fragments appear in the fragment panel. Fragments with interaction energy worse (less negative) than the threshold are hidden. Use when the user says "show only fragments better than -2 kcal/mol" or similar.',
    inputs: [
        param('fragmentInfo', 'object', 'FragmentInfo for which to set the filter', true),
    ],
    outputs: [],
    parameters: [
        param('value', 'number', 'Energy threshold in kcal/mol', true, { defaultValue: 0 }),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_map_available', 'A fragment map must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetFragmentEnergyFilter',
    serverCommand: null,
    jobType: null,
};

export const stepBindingSiteRadius = {
    id: 'step-binding-site-radius',
    name: 'Step Binding Site Radius',
    category: 'Display',
    description: 'Increase or decrease the binding-site bounding-sphere radius by a step amount. Use for fine-tuning the size of the binding pocket sphere shown in the viewer.',
    inputs: [],
    outputs: [],
    parameters: [
        param('delta', 'number', 'Signed step in angstroms (positive expands, negative shrinks)', true, { defaultValue: 1.0 }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'StepBindingSiteRadius',
    serverCommand: null,
    jobType: null,
};

// ─── Workspace extras ──────────────────────────────────────────────────────

export const restoreProtein = {
    id: 'restore-protein',
    name: 'Restore Protein from Saved State',
    category: 'Workspace Management',
    description: 'Restore a protein from a saved capture-state blob into the workspace. Pairs with `capture-state` to round-trip workspace state. The saved state contains the CaseUri, display name, and scoped properties needed to re-load the protein and its associated state.',
    inputs: [
        param('protein', 'object', 'Saved protein blob from capture-state', true),
    ],
    outputs: [
        param('mapCase', 'object', 'The restored MapCase / CaseData reference'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'RestoreProtein',
    serverCommand: null,
    jobType: null,
    // Imminent follow-on adopter per `gibbs-destructive-ops-ux/followups.md`:
    // restoring a saved protein replaces the live workspace state (selections,
    // hidden atoms, scored compounds tied to the live case) with the captured
    // blob. The displaced state cannot be recovered — confirm before dispatch.
    destructiveConfirm: true,
    confirmMessage: 'This will replace the currently loaded protein and any '
        + 'unsaved workspace edits (selections, hidden atoms, scored '
        + 'compounds tied to the live state) with the saved capture. The '
        + 'displaced live state cannot be recovered. Confirm to proceed; '
        + 'cancel to keep your current workspace intact.',
};

// ─── UI panel extras (subsumed if Phase 2 lands first; otherwise here) ─────

export const openSelectBox = {
    id: 'open-select-box',
    name: 'Open Select Box',
    category: 'Workspace Management',
    description: 'Open the residue / atom selection modal. The user picks atoms or residues by selecting from the modal\'s tree, then the selected items become the workspace selection for downstream operations.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenSelectBox',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    setDipoles,
    setFragmentEnergyFilter,
    stepBindingSiteRadius,
    restoreProtein,
    openSelectBox,
];
